import { ClanName, Interpolate, MemberName } from '@lst/mk-react-uikit';
import classnames from 'classnames';
import equal from 'fast-deep-equal/react';
import get from 'lodash/get';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Sticky } from 'react-sticky';

import CopyToClipboardButton from '~/Components/CopyToClipboardButton/CopyToClipboardButton';
import DogTag from '~/Components/DogTag/DogTag';
import { Col, Container, Row } from '~/Components/Grid/Grid';
import InviteButton from '~/Components/InviteButton/InviteButton';
import ProfilePrivacy from '~/Components/ProfilePrivacy/ProfilePrivacy';
import SearchPanel from '~/Components/SearchPanel/SearchPanel';
import { State } from '~/Reducers';
import { isInGame } from '~/utils';
import { formatName } from '~/utils/players';
import { getRoles, permissions } from '~/utils/roles';

import styles from './ProfileHeader.scss';

const isSettingsVisible = window.preloaded.is_visible_settings_show;

export interface ProfileHeaderState {
  ownClanRole?: RoleName;
  hasOwnClan: boolean;
  authSpaId?: number;
  spaId?: string;
  account?: Account;
  clanData?: ClanData;
  isShipTableHeaderSticky: boolean;
}

export interface ProfileHeaderProps {
  noSearch?: boolean;
}

const stateSelector = (state: State): ProfileHeaderState => {
  return {
    ownClanRole: state.ReducerApp.ownClanRole,
    hasOwnClan: state.ReducerApp.hasOwnClan,
    authSpaId: state.ReducerApp.authSpaId,
    account: state.ReducerApp.account,
    clanData: state.ReducerApp.clanData,
    isShipTableHeaderSticky: state.ReducerApp.isShipTableHeaderSticky,
  };
};

const getRegistrationDate = (account: Account | null): string | null => {
  const created_at = account ? get(account, 'statistics.basic.created_at', account.created_at) : null;
  return created_at ? moment(created_at * 1000).format('DD.MM.YYYY') : null;
};

const ProfileHeader = (props: ProfileHeaderProps) => {
  const { noSearch } = props;
  const state = useSelector<State, ProfileHeaderState>(stateSelector, equal);
  const { account, clanData, isShipTableHeaderSticky, authSpaId, spaId, hasOwnClan, ownClanRole } = state;

  const { t } = useTranslation();
  const registrationDate = getRegistrationDate(account);
  const name = formatName(account ? account.name : '');
  const roleName = clanData ? clanData.role : ('' as RoleName);
  const color = get(clanData, 'clan.color', undefined);
  const hexColor = color ? `#${color.toString(16)}` : '#fff';
  const isHidden = account ? account.hidden_profile : false;
  const tag = get(clanData, 'clan.tag', undefined);
  const clanName = get(clanData, 'clan.name', undefined);
  const clanId = get(clanData, 'clan_id', undefined);

  const topOffset = isInGame ? 0.1 : 37;
  const isMyProfile = authSpaId && `${authSpaId}` === spaId;

  const roles = getRoles(t);
  const role = get(roles, roleName, undefined) as Role;
  const ownRole = get(roles, ownClanRole, undefined) as Role;

  const hasPermission = ownRole && ownRole.permissions.includes(permissions.INVITE);

  return (
    <Sticky topOffset={topOffset}>
      {({ style, isSticky }) => {
        const stickyStyle: any = style;
        const wrapper = classnames(styles.wrapper, {
          [styles.stickyWrapper]: isSticky,
          [styles.stickyWrapperShadow]: isSticky && !isShipTableHeaderSticky,
        });
        const dogtag = classnames(styles.dogtag, {
          [styles.dogtagSmall]: isSticky,
        });

        return (
          <div
            style={{
              ...stickyStyle,
              zIndex: 900,
              top: isSticky ? `${topOffset}px` : undefined,
            }}
          >
            <Container className={wrapper} id={'profileHeader'}>
              <Row className={styles.rowWrapper}>
                <Col size={6}>
                  <div className={dogtag}>
                    {!isHidden ? <DogTag clanTag={tag} small={isSticky} /> : null}
                    <div className={styles.accountInfoWrapper}>
                      <div className={styles.nameWrapper}>
                        <div className={styles.name}>{name}</div>
                        <CopyToClipboardButton value={name} />
                      </div>
                      {!isHidden && registrationDate && (
                        <div className={styles.registration}>
                          <Interpolate str={t('Дата регистрации: %(date)s')} date={registrationDate} />
                        </div>
                      )}
                      {account.visibility_settings && isSettingsVisible && (
                        <ProfilePrivacy
                          id={account.id}
                          visibilitySettings={account.visibility_settings}
                          isClanMember={clanId && clanName}
                        />
                      )}
                    </div>
                  </div>
                </Col>
                <Col size={noSearch ? 6 : 3}>
                  <div className={styles.clanData}>
                    {tag && clanName && <ClanName clanColor={hexColor} clanName={clanName} clanTag={tag} />}
                    {!clanId && !isMyProfile && hasOwnClan && hasPermission && <InviteButton />}
                    <MemberName isInline={false} name={role ? role.localized_name : ''} role={role ? role.name : ''} />
                  </div>
                </Col>
                {noSearch ? null : (
                  <Col size={3} className={styles.searchWrapper}>
                    <div className={styles.searchWrapperHeight}>
                      <SearchPanel />
                    </div>
                  </Col>
                )}
              </Row>
            </Container>
          </div>
        );
      }}
    </Sticky>
  );
};

export default ProfileHeader;
